import React, { useMemo, useState } from 'react';
import {
  FaFileAlt,
  FaShieldAlt,
  FaCookieBite,
  FaCalendarAlt,
  FaExternalLinkAlt,
} from 'react-icons/fa';
import {
  TermsDocumentContent,
  PrivacyDocumentContent,
  CookiesDocumentContent,
} from './legal/LegalDocumentContents.tsx';

export type AgreementDocId = 'terms' | 'privacy' | 'cookies';

const DOC_META: Record<
  AgreementDocId,
  { route: string; version: string; lastUpdatedFallback: string }
> = {
  terms: { route: '/terms', version: 'v2.1', lastUpdatedFallback: '18.04.2026' },
  privacy: { route: '/privacy', version: 'v1.8', lastUpdatedFallback: '18.04.2026' },
  cookies: { route: '/cookies-policy', version: 'v1.5', lastUpdatedFallback: '23.02.2026' },
};

type Props = {
  t: (key: string) => string;
};

function EmbeddedLink({
  to,
  className,
  children,
}: {
  to: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <a href={to} className={className} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

export function AgreementSettings({ t }: Props) {
  const [activeDoc, setActiveDoc] = useState<AgreementDocId>('terms');

  const tabs: Array<{ id: AgreementDocId; icon: React.ReactNode; labelKey: string }> = [
    { id: 'terms', icon: <FaFileAlt />, labelKey: 'settings.agreementTabTerms' },
    { id: 'privacy', icon: <FaShieldAlt />, labelKey: 'settings.agreementTabPrivacy' },
    { id: 'cookies', icon: <FaCookieBite />, labelKey: 'settings.agreementTabCookies' },
  ];

  const activeMeta = DOC_META[activeDoc];

  const lastUpdatedDisplay =
    activeDoc === 'cookies'
      ? t('cookiesPolicy.lastUpdated')
      : `${t('settings.agreementLastUpdated')}: ${activeMeta.lastUpdatedFallback}`;

  const docContent = useMemo(() => {
    const sectionClass = 'settings-agreement-doc__section';
    const linkClass = 'settings-agreement-doc__link';
    const introClass = 'settings-agreement-doc__intro';
    if (activeDoc === 'terms') {
      return (
        <TermsDocumentContent
          t={t}
          sectionClass={sectionClass}
          introClass={introClass}
          linkClass={linkClass}
          LinkComponent={EmbeddedLink}
        />
      );
    }
    if (activeDoc === 'privacy') {
      return (
        <PrivacyDocumentContent
          t={t}
          sectionClass={sectionClass}
          introClass={introClass}
          linkClass={linkClass}
          LinkComponent={EmbeddedLink}
        />
      );
    }
    return (
      <CookiesDocumentContent
        t={t}
        sectionClass={sectionClass}
        introClass={introClass}
        subblockClass="settings-agreement-doc__subblock"
      />
    );
  }, [activeDoc, t]);

  const handleOpenExternal = () => {
    window.open(activeMeta.route, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="settings-container settings-agreement">
      <h1 className="settings-title">{t('settings.agreementTitle')}</h1>
      <p className="settings-subtitle settings-agreement-intro">{t('settings.agreementIntro')}</p>

      <div className="settings-agreement-tabs" role="tablist" aria-label={t('settings.agreementTitle')}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeDoc === tab.id}
            className={`settings-agreement-tab${activeDoc === tab.id ? ' is-active' : ''}`}
            onClick={() => setActiveDoc(tab.id)}
          >
            <span className="settings-agreement-tab__icon" aria-hidden>{tab.icon}</span>
            <span className="settings-agreement-tab__label">{t(tab.labelKey)}</span>
          </button>
        ))}
      </div>

      <div className="settings-agreement-viewer" role="tabpanel">
        <div className="settings-agreement-doc-scroll">{docContent}</div>
      </div>

      <div className="settings-agreement-meta">
        <div className="settings-agreement-meta__item">
          <FaCalendarAlt aria-hidden />
          <span>{lastUpdatedDisplay}</span>
        </div>
        <div className="settings-agreement-meta__item">
          <span>{t('settings.agreementDocVersion')}</span>
          <span className="settings-agreement-meta__badge">{activeMeta.version}</span>
        </div>
      </div>

      <div className="settings-agreement-actions">
        <button type="button" className="btn btn-secondary" onClick={handleOpenExternal}>
          <FaExternalLinkAlt aria-hidden />
          {t('settings.agreementOpenNew')}
        </button>
      </div>
    </div>
  );
}
